/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { PALETTE } from "styles/base/variables";
import { ReactComponent as IconChevronDown } from "assets/img/icon/chevron-md-down.svg";
import { useState } from "react";
import Backdrop from "components/common/Backdrop";
import OptionBox from "components/common/Option/Box";

export interface ExtendedOption
  extends React.OptionHTMLAttributes<ExtendedOption> {
  value: string | number;
  label: string;
}

interface BaseSelectProps {
  variant?: "filled" | "outlined" | "standard";
  name: string;
  color?: string;
  disabled?: boolean;
  minWidth?: number;
  maxHeight?: number;
  selectedOption: string;
  options: ExtendedOption[];
  onSelect?: (e: React.MouseEvent<HTMLOptionElement>) => void;
}

const roateAnimationCss = `
  @keyframes rotate1 {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(180deg);
    }
  }

  @keyframes rotate2 {
    from {
      transform: rotate(180deg);
    }
    to {
      transform: rotate(0deg);
    }
  }
`;

function BaseSelect({
  variant: _variant = "outlined",
  name,
  color,
  disabled,
  minWidth = 120,
  maxHeight = 240,
  selectedOption,
  options,
  onSelect,
  ...props
}: BaseSelectProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const clickOption = (e: React.MouseEvent<HTMLOptionElement>) => {
    if (onSelect) onSelect(e);
    setIsOpen(false);
  };

  const renderSelectedOption = (val: string) => {
    if (!val) return name;
    const selected = options.find((option) => option.value == val);
    if (!selected) return name;
    else return selected.label;
  };

  const defaultCss = `
    min-width: ${minWidth}px;
    max-height: ${maxHeight}px;
    padding: 12px 14px;
    font-size: 14px;
    transition: background 0.3s;
    display: flex;
    justify-content: space-between;
    align-items: center;
  `;

  const filledCss = `
   ${defaultCss}
  `;

  const outlinedCss = `
    ${defaultCss}
    border: 1px solid ${
      disabled ? PALETTE.textDisabled : color || PALETTE.border
    };
    border-radius: 4px;
    background-color: ${PALETTE.light};
    color: ${disabled ? PALETTE.textDisabled : color || PALETTE.text};
  `;

  const standardCss = `
    ${defaultCss}
    border-top: none;
    border-right: none;
    border-left: none;
    border-radius: 4px 4px 0 0;
    border-width: 1px;
  `;

  const arrowAnimationCss = (isOpen: boolean | null) => {
    let arrCss = "";
    if (isOpen === null) return arrCss;
    else arrCss += roateAnimationCss;

    if (isOpen) arrCss += "animation: rotate1 0.3s forwards;";
    else arrCss += "animation: rotate2 0.3s forwards;";

    return arrCss;
  };

  const cssRenderer = (variant: string) => {
    switch (variant) {
      case "filled":
        return filledCss;
      case "outlined":
        return outlinedCss;
      case "standard":
        return standardCss;
      default:
        return outlinedCss;
    }
  };

  return (
    <>
      <div>
        <button
          css={css`
            ${cssRenderer(_variant)}
            anchor-name: --select-button-${name};
          `}
          disabled={disabled}
          {...props}
          onClick={() => setIsOpen(!isOpen)}
        >
          {renderSelectedOption(selectedOption)}
          <IconChevronDown
            stroke={disabled ? PALETTE.textDisabled : color || PALETTE.text}
            css={css`
              ${arrowAnimationCss(isOpen)}
            `}
          />
        </button>
        <OptionBox
          isOpen={isOpen}
          minWidth={minWidth}
          maxHeight={maxHeight}
          name={name}
          color={color}
          options={options}
          clickOption={clickOption}
        />
      </div>
      <Backdrop isOpen={isOpen} setIsOpen={setIsOpen} />
    </>
  );
}

export default BaseSelect;
